import { useReducer, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate, resolvePath } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Menu, Item } from 'react-contexify';
import { useApolloClient, useLazyQuery, useMutation } from '@apollo/client';
import 'react-contexify/dist/ReactContexify.css';
import { ErrorBoundary } from 'react-error-boundary';
import { useAuth } from '../../hooks/useAuth';
import { canUseModelFiles } from '../../utils';

import '../../styles/reactContexifyOverrides.css';

import CheckboxInput from '../../components/inputs/CheckboxInput';
import uploadIcon from '../../assets/upload-icon.svg';
import DraggableQueryList from '../../components/builder/DraggableQueryList';
import UploadedFilesList from '../../components/builder/UploadedFilesList';
import {
  AUDIENCES_QUERY,
  PURCHASED_AUDIENCES_QUERY,
  GET_COUNT_QUERY,
  AUDIENCE_QUERY,
  FILES_QUERY,
} from '../../queries';
import ActionModal from '../../components/ActionModal';
import ToastNotification from '../../components/ToastNotification';
import FileStatusBadge from '../../components/FileStatusBadge';
import { FileImportSelector } from '../../components/actions/FileImportSelector';
import {
  CLEAR_SUCCESS,
  initialState,
  reducer,
  SET_SUCCESS,
  START_ACTION,
} from '../../reducers/actionReducer';
import DataDictionary from '../../components/builder/DataDictionary';
import VariablesDropzone from '../../components/builder/VariablesDropzone';
import PlainLanguageSummary, {
  collectSummaryText,
} from '../../components/builder/PlainLanguageSummary';
import {
  ADD_ATTRIBUTE,
  ADD_MY_DATA_RESOURCE,
  AUDIENCE_NAME_CHANGE,
  CHECKBOX_CHANGE,
  initialState as builderInitialState,
  reducer as builderReducer,
  TYPES,
  LOAD_SAVED_AUDIENCE,
  LOAD_TOUR_SAMPLE,
  SET_RECORDS_AVAILABLE,
  SET_SAVED_AUDIENCE,
  RESET,
  SET_AUDIENCE_NAME_ERROR,
} from '../../reducers/builderReducer';
import TourProvider from '../../components/tour/TourProvider';
import useTour from '../../components/tour/useTour';
import GetStartedBanner from '../../components/tour/GetStartedBanner';
import {
  PLATFORM_INTRO_TOUR_ID,
  platformIntroTour,
} from '../../components/tour/tours/platformIntroTour';
import { buildAndOrTourSampleTree } from '../../components/tour/sampleAndOrExpression';
import AudienceNameForm from '../../components/builder/AudienceNameForm';
import MyAudience from '../../components/builder/MyAudience';
import {
  convertBuilderStateToFilters,
  convertFiltersToBuilderFormat,
  canAddAttribute,
  filePrefixesMap,
  getIncludedIdentifiersAvailable,
} from '../../helpers/dataDictionary';
import {
  getZoneVariables,
  isEmpty as isExpressionEmpty,
  findFileLeafByFileId,
  findMembershipLeaf,
} from '../../helpers/expressionTree';
import useNavigationPrompt from '../../hooks/useNavigationPrompt';
import NavigationPromptModal from '../../components/builder/NavigationPromptModal';
import SavedAudiencesList from '../../components/builder/SavedAudiencesList';
import DeleteAudience from '../../components/actions/DeleteAudience';
import DeleteFile from '../../components/actions/DeleteFile';
import ErrorText from '../../components/ErrorText';
import useAudienceNameValidation from '../../hooks/useAudienceNameValidation';
import UploadFileSuccess from '../../components/actions/UploadFileSuccess';
import InsetLoadingOverlay from '../../components/InsetLoadingOverlay';
import BuilderErrorFallback from '../../components/errors/BuilderErrorFallback';
import BuilderHelpfulTipsContainer from '../../components/builder/BuilderHelpfulTipsContainer';
import AudienceWarningModal, {
  UNCHANGED_NAME_WARNING,
} from '../../components/builder/AudienceWarningModal';
import {
  findFullRangeModels,
  findWideOpenFilters,
  fullRangeModelWarning,
  isOversizedAudience,
  universeShare,
  wideOpenSignature,
  type BreadthIssue,
} from '../../helpers/audienceBreadth';
import useAudienceUniverseSize from '../../hooks/useAudienceUniverseSize';
import { SAVE_AUDIENCE, UPDATE_RECORDS_INCLUDED } from '../../mutations';
import BugReporter from "../../components/BugReporter";
import DocsBotChatbot from '../../components/builder/DocsBotChatbot';

const { VARIABLE_ATTRIBUTE, MY_DATA_ATTRIBUTE } = TYPES;

type SidebarFile = {
  kind?: string | null;
  createdAt: string;
  status?: string | null;
};

/** A sidebar list's filterItems: files of one kind only, newest first. */
const filesOfKindNewestFirst = (kind: string) => (files: SidebarFile[]) =>
  files
    .filter((file) => file.kind === kind)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

/** ATAD-136: the "ZIP Code Files" sidebar list — ZIP files only, newest first. */
const zipCodeFilesNewestFirst = filesOfKindNewestFirst('zip');

/** ATAD-146: the "Model Files" sidebar list — model files only, newest first. */
const modelFilesNewestFirst = filesOfKindNewestFirst('model');

/** ATAD-146: a model import still queued or running. */
const isModelFileImporting = (file: SidebarFile) =>
  file.status === 'pending' || file.status === 'processing';

/**
 * ATAD-146: keep polling the Model Files list while an import runs, so the
 * model turns draggable (its crawledAt is set) without a page refresh.
 */
const anyModelFileImporting = (files: SidebarFile[]) =>
  files.some(isModelFileImporting);

/** ATAD-146: why a model in the sidebar is greyed out (queued, importing, failed). */
const modelFileStatus = (file: SidebarFile) =>
  file.status && file.status !== 'completed' ? (
    <FileStatusBadge status={file.status} kind="model" />
  ) : null;

/**
 * ATAD-146: a saved audience's filters as builder trees, or why they can't be
 * loaded. convertFiltersToBuilderFormat throws on a filter it can't represent
 * (an unknown leaf kind, a model without a valid range) instead of dropping
 * it: loading the rest would show — and let the user re-save — a different
 * audience. The caller reports the problem and loads nothing.
 */
function readSavedFilters(filters: string): {
  loaded: ReturnType<typeof convertFiltersToBuilderFormat> | null;
  problem: string;
} {
  try {
    const loaded = convertFiltersToBuilderFormat(filters);
    // undefined: the filters weren't valid JSON (logged by the loader).
    return { loaded: loaded ?? null, problem: "We couldn't read this audience's filters." };
  } catch (err) {
    return { loaded: null, problem: (err as Error).message };
  }
}

/** ATAD-146: delete-dialog titles for the file kinds the sidebar lists. */
const DELETE_FILE_TITLES: Record<string, string> = {
  file: 'Delete File',
  zip: 'Delete ZIP Code File',
  model: 'Delete Model File',
};

// ── ATAD-134 warning copy ───────────────────────────────────────────────────
// Module scope: pure functions of their arguments, and keeping them out of the
// component body avoids rebuilding the JSX on every unrelated render.

/**
 * Turn wide-open filters into warning list items.
 *
 * Says "isn't narrowing" rather than "has no effect": include ranges compile to
 * `try_cast(col as integer) BETWEEN min AND max`, which drops NULLs, so a
 * full-range filter is not strictly a no-op.
 */
function buildWideOpenWarnings(issues: BreadthIssue[] | null) {
  return (issues ?? []).map((issue) => ({
    id: issue.key,
    title: issue.attributeName,
    body:
      issue.kind === 'model-full-range'
        ? fullRangeModelWarning(issue)
        : issue.kind === 'wide-open-range'
          ? `set to its full range (${issue.detail.min?.toLocaleString()}–${issue.detail.max?.toLocaleString()})`
          : `all ${issue.detail.total} options selected`,
  }));
}

/**
 * Heading and lead-in for the wide-open dialog. The original copy is kept
 * for include-only issues; ATAD-146 adds exclude-zone model issues, which
 * fail the opposite way — they empty the audience rather than not narrow it.
 */
function wideOpenDialogCopy(issues: BreadthIssue[] | null) {
  if (!(issues ?? []).some((issue) => issue.zoneType === 'exclude')) {
    return {
      heading: "Some filters aren't narrowing your audience",
      intro:
        "These filters are set to their full range, so they include everyone. That's fine if it's intentional — but it usually means a filter was dropped in and never adjusted.",
    };
  }
  return {
    heading: 'Some filters are set to their full range',
    intro:
      "In Include, a filter at its full range doesn't narrow your audience. In Exclude, a model at its full range removes everyone the model scores — nearly everyone. That's fine if it's intentional — but it usually means a filter was dropped in and never adjusted.",
  };
}

/**
 * Copy for an audience covering an implausible share of the US universe.
 *
 * The universe figure is an env-configured estimate, so everything here is
 * rounded and hedged — no false precision. The >= 100% branch matters: the
 * count can exceed a stale or deliberately-lowered configured value (it is set
 * to a tiny number in local testing), and "about 340% of the US" reads as a bug.
 */
function buildOversizeWarnings(
  sharePct: number | null,
  universeSize: number | null
) {
  const millions =
    universeSize == null ? null : Math.round(universeSize / 1_000_000);

  const lead =
    sharePct != null && sharePct >= 100 ? (
      <p className="text-gray-600">
        Your filters match more people than our estimate of the entire US
        consumer universe. That almost always means a filter was left wide open.
      </p>
    ) : (
      <p className="text-gray-600">
        Your filters match about <span className="font-bold">{sharePct}%</span>{' '}
        of the US consumer universe
        {millions != null ? ` — roughly ${millions} million people` : ''}.
        Audiences this broad are usually the result of a filter left wide open.
      </p>
    );

  return [
    {
      id: 'oversized-audience',
      body: (
        <>
          {lead}
        </>
      ),
    },
  ];
}

// GetStartedTourBanner is rendered inside <TourProvider> so it can reach the
// start() function via useTour. Kept in this file so the JSX block below
// stays readable; lift if more tours/banners are added.

function GetStartedTourBanner() {
  const { start } = useTour();
  return <GetStartedBanner onTakeTour={() => start(PLATFORM_INTRO_TOUR_ID)} />;
}

export default function AudienceBuilder({ resetBuilder }) {
  const navigate = useNavigate();
  const location = useLocation();
  const client = useApolloClient();
  const { user } = useAuth();

  const initialLoadState = location.state?.initialLoadState;

  const [actionState, dispatchAction] = useReducer(reducer, initialState);
  const { success } = actionState;

  const [builderState, builderDispatch] = useReducer(
    builderReducer,
    builderInitialState
  );

  const [contextMenuError, setContextMenuError] = useState<
    string | undefined
  >();
  const [showAddGeoHelpfulTip, setShowAddGeoHelpfulTip] =
    useState<boolean>(false);
  const [showUnchangedNameModal, setShowUnchangedNameModal] = useState(false);

  // ── ATAD-134 oversized-audience warnings ──────────────────────────────────
  // Wide-open filters at Get counts; an implausible share of the US universe
  // at Continue Order. Both advisory — the user can proceed.
  //
  // `null` means "modal closed" in both cases, so there is no separate boolean
  // that could drift out of sync with the payload it gates.
  const [wideOpenIssues, setWideOpenIssues] = useState<BreadthIssue[] | null>(
    null
  );
  const [pendingBuyRecords, setPendingBuyRecords] = useState<number | null>(
    null
  );

  // Acknowledgements are content-addressed, not booleans: we warn once per
  // DISTINCT problem. Refs rather than state because they are never rendered
  // and the proceed handlers must read what they just wrote in the same tick.
  //
  // Reset covers itself — AudienceBuilderWrapper remounts this component via
  // `key`, so both refs are recreated. Loading a saved audience does NOT
  // remount, and is cleared explicitly in loadAudienceFn.
  const acknowledgedBreadthRef = useRef<string | null>(null);
  const oversizeAckRef = useRef<number | null>(null);

  const universeSize = useAudienceUniverseSize();

  // ── Platform intro tour registration ──────────────────────────────────────
  // The tour fires only via the Get started banner CTA — we deliberately
  // don't auto-start, because the banner already covers the "first visit"
  // prompt and an auto-start would overlap with the banner (and race against
  // saved-audience loads).
  //
  // onStart reads the freshest expression via a ref and seeds a canned
  // AND/OR tree only when the Include zone is empty — pre-existing user
  // work is preserved.
  const expressionRef = useRef(builderState.expression);
  useEffect(() => {
    expressionRef.current = builderState.expression;
  });

  const tours = useMemo(
    () => [
      {
        ...platformIntroTour,
        onStart: () => {
          if (isExpressionEmpty(expressionRef.current)) {
            builderDispatch({
              type: LOAD_TOUR_SAMPLE,
              payload: { tree: buildAndOrTourSampleTree() },
            });
          }
        },
      },
    ],
    // builderDispatch is stable (useReducer guarantee). Memoize once so
    // TourProvider doesn't tear down its registry on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  function handleAttributeClick({ props, data }) {
    const { zoneType } = data;
    const { name, id, allowedZoneTypes } = props;

    if (!allowedZoneTypes.includes(zoneType)) {
      setContextMenuError(
        'Identifiers are only allowed in the Include section.'
      );
      return;
    }

    const otherZone = zoneType === 'include' ? 'exclude' : 'include';
    if (
      !canAddAttribute(getZoneVariables(builderState, zoneType), id) ||
      !canAddAttribute(getZoneVariables(builderState, otherZone), id)
    ) {
      setContextMenuError(
        id?.toLowerCase()?.includes('geography')
          ? 'Only one geography variable is allowed at once.'
          : 'Attribute has already been added.'
      );
      return;
    }

    builderDispatch({
      type: ADD_ATTRIBUTE,
      payload: {
        name,
        id,
        zoneType,
      },
    });
  }

  function handleDeleteClick({ props }) {
    const { name, id, resourceType } = props;

    // files (ID files, ATAD-136 ZIP code files and ATAD-146 model files share
    // the delete flow; the server refuses a ZIP or model file still used by
    // an unpurchased saved audience, and a model whose import is running)
    if (resourceType === 'file' || resourceType === 'zip' || resourceType === 'model') {
      dispatchAction({
        type: START_ACTION,
        payload: {
          name: DELETE_FILE_TITLES[resourceType],
          component: (
            <DeleteFile
              dispatchAction={dispatchAction}
              fileName={name}
              fileId={id}
            />
          ),
        },
      });
      return;
    }

    // saved audiences
    if (resourceType === 'audience') {
      dispatchAction({
        type: START_ACTION,
        payload: {
          name: 'Delete Audience',
          component: (
            <DeleteAudience
              dispatchAction={dispatchAction}
              audienceName={name}
            />
          ),
        },
      });
      return;
    }
  }

  function handleMyDataResourceClick({ props, data }) {
    const { zoneType } = data;
    const { id, name, resourceType, disabled } = props;

    // ATAD-146: the menu opens on a greyed-out model too. One still
    // importing (or failed) has no data to filter by yet — say so here
    // rather than let Get counts fail on it.
    if (resourceType === 'model' && disabled) {
      setContextMenuError(
        `${name} can't be used until it has finished importing.`
      );
      return;
    }

    // PR 2c: membership lives as leaves in the expression trees, not in the
    // retired myData arrays. Treat the resource as "already added" if it
    // appears in either zone's tree (mirrors canAddFileToZone, generalized to
    // audiences). The old canAddMyDataResource(builderState[myDataZoneType(...)])
    // dereferenced a now-undefined array and threw.
    const resourceInTree = (tree) =>
      Boolean(findMembershipLeaf(tree, resourceType, id));
    if (
      resourceInTree(builderState.expression) ||
      resourceInTree(builderState.excludeExpression)
    ) {
      setContextMenuError('Resource has already been added.');
      return;
    }

    builderDispatch({
      type: ADD_MY_DATA_RESOURCE,
      payload: {
        id,
        name,
        type: resourceType,
        zoneType,
      },
    });
  }

  // Eligibility check used by the Uploaded Files modal so each row knows
  // whether its "Add to Include / Exclude" buttons should be enabled.
  // PR 2c moved file/audience membership from dedicated myData arrays into
  // leaves inside the unified `expression` / `excludeExpression` trees, so
  // we walk those trees here rather than calling the (legacy)
  // canAddMyDataResource helper. A file is considered "already added" if it
  // appears anywhere in either zone.
  function canAddFileToZone(
    fileId: string,
    _zoneType: 'include' | 'exclude'
  ): boolean {
    return (
      !findFileLeafByFileId(builderState.expression, fileId) &&
      !findFileLeafByFileId(builderState.excludeExpression, fileId)
    );
  }

  // Click-driven equivalent of the right-click "Add to Include / Exclude"
  // context menu used on files dragged from the sidebar. The modal uses this
  // since drop targets are obscured while the modal is open.
  function handleAddFileFromModal(
    file: { id: string; name: string },
    zoneType: 'include' | 'exclude'
  ) {
    if (!canAddFileToZone(file.id, zoneType)) {
      setContextMenuError('Resource has already been added.');
      return;
    }

    builderDispatch({
      type: ADD_MY_DATA_RESOURCE,
      payload: {
        id: file.id,
        name: file.name,
        type: 'file',
        zoneType,
      },
    });
  }

  const [
    loadAudience,
    {
      loading: loadingLoadAudience,
      error: errorLoadAudience,
      called: calledLoadAudience,
      data: dataLoadAudience,
    },
  ] = useLazyQuery(AUDIENCE_QUERY, {
    fetchPolicy: 'network-only',
  });

  const loadAudienceFn = useCallback(
    (name) => {
      // A saved-audience load dispatches RESET without remounting, so the
      // ATAD-134 acknowledgements have to be cleared by hand — otherwise a
      // warning dismissed on the previous audience would suppress the same
      // warning on this one.
      acknowledgedBreadthRef.current = null;
      oversizeAckRef.current = null;
      loadAudience({
        variables: {
          audienceName: name,
        },
        onCompleted: (data) => {
          builderDispatch({
            type: SET_RECORDS_AVAILABLE,
            payload: {
              recordsAvailable: data.audience.recordsAvailable,
              loadingInitialState: true,
            },
          });
        },
      });
    },
    [loadAudience]
  );

  function handleSavedAudienceClick(name) {
    if (
      hasStateChanged ||
      (builderState.hasGottenCounts && !builderState.isSavedAudience)
    ) {
      setShowLoadPrompt(true);
      setConfirmLoadPrompt({
        loadFn: () => {
          builderDispatch({ type: RESET });
          loadAudienceFn(name);
        },
        label: 'Load',
      });
      return;
    }
    builderDispatch({ type: RESET });
    loadAudienceFn(name);
  }

  useEffect(() => {
    if (
      dataLoadAudience &&
      dataLoadAudience.audience &&
      calledLoadAudience &&
      dataLoadAudience.audience.id !== builderState.audienceId
    ) {
      const { id, name, recordsAvailable, recordsIncluded, filters } =
        dataLoadAudience.audience;
      // PR 2c: convertFiltersToBuilderFormat now folds the legacy
      // top-level audiences/files arrays into the expression trees as
      // membership leaves. No more myDataInclude/myDataExclude.
      const { loaded, problem } = readSavedFilters(filters);
      if (!loaded) {
        setContextMenuError(problem);
        return;
      }
      const {
        expression,
        excludeExpression,
        includeOnlyOneRecordPerHousehold,
      } = loaded;

      builderDispatch({
        type: LOAD_SAVED_AUDIENCE,
        payload: {
          initialLoadState: {
            audienceId: id,
            audienceName: name,
            incomingRecordsIncluded: recordsIncluded,
            hasGottenCounts: true,
            recordsAvailable,
            expression,
            excludeExpression,
            includeOnlyOneRecordPerHousehold,
          },
        },
      });
    }
  }, [dataLoadAudience, calledLoadAudience]);

  useEffect(() => {
    if (
      initialLoadState &&
      initialLoadState.audienceName &&
      !initialLoadState.creatingNewAudience
    ) {
      loadAudienceFn(initialLoadState.audienceName);
      initialLoadState.audienceName = null;
    }

    if (initialLoadState && initialLoadState.creatingNewAudience) {
      const { filters, audienceId, audienceName, newAudienceName } =
        initialLoadState;
      const { loaded, problem } = readSavedFilters(filters);
      if (!loaded) {
        setContextMenuError(problem);
        // Clear the request so the effect doesn't retry it on every render.
        initialLoadState.audienceName = null;
        initialLoadState.creatingNewAudience = null;
        return;
      }
      const {
        expression,
        excludeExpression,
        includeOnlyOneRecordPerHousehold,
      } = loaded;

      // PR 2c: the parent audience used to be appended to myDataExclude
      // as `{id, name, type: 'audience'}`. With membership leaves now
      // living in the tree, append it as an audience leaf in the exclude
      // expression instead. Mirrors the previous "exclude this seed
      // audience" semantics.
      const excludeWithParent = (() => {
        const parentLeaf = {
          type: 'leaf' as const,
          kind: 'audience' as const,
          // nodeId will be assigned by the reducer's LOAD path or by the
          // tree helper when it merges. We use a stable client id here.
          nodeId: `parent-${audienceId}`,
          audienceId,
          name: audienceName,
        };
        if (!excludeExpression) return parentLeaf;
        if (
          excludeExpression.type === 'and' &&
          'children' in excludeExpression
        ) {
          return {
            ...excludeExpression,
            children: [...excludeExpression.children, parentLeaf],
          };
        }
        return {
          type: 'and' as const,
          nodeId: `wrap-${audienceId}`,
          children: [excludeExpression, parentLeaf],
        };
      })();

      builderDispatch({
        type: LOAD_SAVED_AUDIENCE,
        payload: {
          initialLoadState: {
            audienceName: newAudienceName,
            isSavedAudience: false,
            hasStateChanged: true,
            expression,
            excludeExpression: excludeWithParent,
            includeOnlyOneRecordPerHousehold,
          },
        },
      });
      initialLoadState.audienceName = null;
      initialLoadState.creatingNewAudience = null;
    }

    if (
      initialLoadState &&
      !initialLoadState.audienceName &&
      initialLoadState.initialFileInclude
    ) {
      const { id, name } = initialLoadState.initialFileInclude;

      // PR 2c retired the `myDataInclude`/`myDataExclude` arrays — membership
      // now lives as leaves in the expression trees. `canAddFileToZone` walks
      // those trees; the old `canAddMyDataResource(builderState[myDataZoneType(
      // 'include')], ...)` dereferenced a now-undefined array and threw
      // (crashing the builder and aborting its in-flight queries → nginx 499).
      if (canAddFileToZone(id, 'include')) {
        builderDispatch({
          type: ADD_MY_DATA_RESOURCE,
          payload: {
            id,
            name,
            type: 'file',
            zoneType: 'include',
          },
        });
        initialLoadState.initialFileInclude = null;
      }
    }
  }, [builderState, initialLoadState, loadAudience, loadAudienceFn]);

  const [getCount, { loading: loadingGetCount, error: errorGetCount }] =
    useLazyQuery(GET_COUNT_QUERY, {
      fetchPolicy: 'network-only',
    });

  // Fires the count query. Split out of handleGetCountsClick by ATAD-134 so
  // the wide-open-filter warning can sit in front of it without duplicating
  // the query. Deliberately re-invoked from the modal's proceed handler rather
  // than parked as a thunk: this closes over builderState, and a closure
  // captured at modal-open time could fire a query for a stale audience.
  const runGetCounts = useCallback(() => {
    try {
      const formattedFilters = convertBuilderStateToFilters(builderState);
      getCount({
        variables: {
          filters: JSON.stringify(formattedFilters),
        },
        onCompleted: (data) => {
          builderDispatch({
            type: SET_RECORDS_AVAILABLE,
            payload: {
              recordsAvailable: data.getCount,
            },
          });
          if (
            !getZoneVariables(builderState, 'include').some(({ id }) =>
              id.toLowerCase().includes('___geography___'),
            )
          ) {
            setShowAddGeoHelpfulTip(false);
          }
        },
        onError: (err) => {
          setContextMenuError(err.message);
        },
      });
    } catch (err) {
      setContextMenuError(err.message);
    }
  }, [builderState, getCount]);

  const handleGetCountsClick = useCallback(() => {
    // The guard this replaces destructured include/exclude/myDataInclude/
    // myDataExclude off builderState. PR 2c moved those into the expression
    // trees, so all four read undefined, `undefined?.length === 0` was false,
    // and the guard never fired — an empty builder sent an unfiltered query
    // to Athena. isExpressionEmpty is the tree-aware equivalent.
    if (
      isExpressionEmpty(builderState.expression) &&
      isExpressionEmpty(builderState.excludeExpression)
    ) {
      setContextMenuError(
        'Please include or exclude at least one element for filtering. Try dragging in an attribute from the data dictionary.'
      );
      return;
    }

    // ATAD-134 option 1. Warn once per distinct set of wide-open filters:
    // Range cards seed themselves at their full domain, so an attribute that
    // was dragged in and never touched is wide open, and an un-deduped warning
    // would fire on nearly every click.
    // ATAD-146: plus model files at their full 1–20 range, in BOTH zones (a
    // full-range model in Exclude removes nearly everyone). Variables are
    // still checked in Include only.
    const issues = [
      ...findWideOpenFilters(builderState, { zones: ['include'] }),
      ...findFullRangeModels(builderState),
    ];
    if (
      issues.length > 0 &&
      wideOpenSignature(issues) !== acknowledgedBreadthRef.current
    ) {
      setWideOpenIssues(issues);
      return;
    }

    runGetCounts();
  }, [builderState, runGetCounts]);

  const handleWideOpenProceed = useCallback(() => {
    acknowledgedBreadthRef.current = wideOpenSignature(wideOpenIssues ?? []);
    setWideOpenIssues(null);
    runGetCounts();
  }, [wideOpenIssues, runGetCounts]);

  const [
    saveAudience,
    { loading: loadingSaveAudience, error: errorSaveAudience },
  ] = useMutation(SAVE_AUDIENCE, {
    fetchPolicy: 'network-only',
  });

  const handleSaveAudienceClick = useCallback(
    (
      audienceNameInput: string,
      // PR 2d: callback receives the freshly-saved audienceId so that
      // downstream navigation can include it on orderDetails. Reading
      // builderState.audienceId from a closure here would be stale —
      // SET_SAVED_AUDIENCE has been dispatched but React hasn't re-rendered
      // before the callback runs, so the closure still sees `undefined`.
      callbackFn?: (audienceId?: string) => void,
    ) => {
      try {
        const formattedFilters = convertBuilderStateToFilters(builderState);
        // ATAD-127: the form can submit without ever blurring, so this
        // value hasn't necessarily been through AUDIENCE_NAME_CHANGE.
        // Normalize before it reaches either the mutation or the post-save
        // dispatch below — writing the raw string back into builderState
        // is how an untrimmed name used to reach openOrder.
        const sanitizedAudienceName =
          typeof audienceNameInput === 'string'
            ? audienceNameInput.trim()
            : audienceNameInput;
        saveAudience({
          variables: {
            audienceName: builderState.audienceName,
            filters: JSON.stringify(formattedFilters),
            recordsIncluded: builderState.recordsIncluded,
          },
          onCompleted: (data) => {
            if (data && data.saveAudience) {
              client.refetchQueries({
                include: [AUDIENCES_QUERY, PURCHASED_AUDIENCES_QUERY],
              });
              builderDispatch({
                type: SET_SAVED_AUDIENCE,
                payload: {
                  audienceId: data.saveAudience,
                  audienceName: sanitizedAudienceName,
                },
              });
              dispatchAction({
                type: SET_SUCCESS,
                payload: {
                  message: 'Saved audience successfully',
                },
              });
              if (typeof callbackFn === 'function') {
                callbackFn(data.saveAudience);
              }
            }
          },
          onError: (err) => {
            setContextMenuError(err.message);
          },
        });
      } catch (err) {
        setContextMenuError(err.message);
      }
    },
    [builderState, saveAudience]
  );

  const [
    updateRecordsIncluded,
    {
      loading: loadingUpdateRecordsIncluded,
      error: errorUpdateRecordsIncluded,
    },
  ] = useMutation(UPDATE_RECORDS_INCLUDED, {
    fetchPolicy: 'network-only',
  });

  const { audienceWithNameAlreadyExists } = useAudienceNameValidation(
    builderState.audienceName
  );

  const hasStateChanged = useMemo(
    () => builderState.hasStateChanged,
    [builderState.hasStateChanged]
  );

  const canSaveOrBuy = useMemo(
    () =>
      !builderState.hasStateChanged &&
      builderState.hasGottenCounts &&
      !builderState.adjustmentError,
    [
      builderState.hasGottenCounts,
      builderState.hasStateChanged,
      builderState.adjustmentError,
    ]
  );

  // ATAD-134. The Records Available tile keeps showing its last value after a
  // filter edit (no reducer case clears it), so gating on !hasStateChanged is
  // what stops the badge asserting "half the US" about a number that no longer
  // describes the audience. That works because SET_RECORDS_AVAILABLE is itself
  // what clears hasStateChanged — if that ever changes, this gate breaks too.
  const showOversizeBadge = useMemo(
    () =>
      !builderState.hasStateChanged &&
      builderState.hasGottenCounts &&
      isOversizedAudience(builderState.recordsAvailable, universeSize),
    [
      builderState.hasGottenCounts,
      builderState.hasStateChanged,
      builderState.recordsAvailable,
      universeSize,
    ]
  );

  const universeSharePct = useMemo(() => {
    const share = universeShare(builderState.recordsAvailable, universeSize);
    return share == null ? null : Math.round(share * 100);
  }, [builderState.recordsAvailable, universeSize]);

  const recordsAvailable = useMemo(
    () => builderState.recordsAvailable,
    [builderState.recordsAvailable]
  );

  const recordsIncluded = useMemo(
    () => builderState.recordsIncluded,
    [builderState.recordsIncluded]
  );

  const error = useMemo(
    () =>
      errorSaveAudience?.message ||
      errorLoadAudience?.message ||
      errorGetCount?.message ||
      errorUpdateRecordsIncluded?.message,
    [errorSaveAudience, errorLoadAudience, errorGetCount]
  );

  const loading = useMemo(
    () =>
      loadingSaveAudience ||
      loadingLoadAudience ||
      loadingGetCount ||
      loadingUpdateRecordsIncluded,
    [loadingSaveAudience, loadingLoadAudience, loadingGetCount]
  );

  const shouldBlockNavigation = useMemo(
    () => !!(hasStateChanged || loading),
    [hasStateChanged, loading]
  );

  const [showPrompt, confirmNavigation, cancelNavigation] = useNavigationPrompt(
    shouldBlockNavigation,
    [{ path: resolvePath('../order', location.pathname)?.pathname, end: false }]
  );

  const [showLoadPrompt, setShowLoadPrompt] = useState();
  const [confirmLoadPrompt, setConfirmLoadPrompt] = useState();

  const navigateToPurchasePage = useCallback(
    // PR 2d: accept an optional override for the saved audienceId so the
    // first-save-then-buy path can pass through the id returned by the
    // saveAudience mutation, bypassing the stale-closure problem where
    // builderState.audienceId is still `undefined` at the time React
    // re-runs this callback. For already-saved audiences we still fall
    // back to builderState.audienceId.
    (recordsIncluded, audienceIdOverride?: string) => {
      // PR 2d: snapshot the at-this-moment plain-language summary so the
      // downstream openOrder mutation can persist it on the Order. Riding
      // along in orderDetails preserves the audience-as-shown for
      // debugging even if the underlying Audience doc is later edited.
      const audiencePlainText = collectSummaryText(
        builderState.expression,
        builderState.excludeExpression,
      );
      const audienceIdForOrder =
        audienceIdOverride ?? builderState.audienceId;
      navigate('../order/upsell', {
        state: {
          from: location,
          orderDetails: {
            coreProductName: 'audience',
            audienceName: builderState.audienceName,
            // PR 2d: include the saved Audience's id so the server can
            // fetch its filters and persist the snapshot. Prefer the
            // override passed in from a fresh save (see comment above)
            // over the possibly-stale builderState value.
            audience: audienceIdForOrder,
            audiencePlainText,
            recordsIncluded,
          },
          identifiersInitialState: getIncludedIdentifiersAvailable(
            getZoneVariables(builderState, 'include'),
          ),
        },
      });
    },
    [builderState, location, navigate]
  );

  // The order path itself, unchanged. Extracted from handleBuyClick by
  // ATAD-134 so the oversized-audience warning can sit in front of it.
  const proceedWithBuy = useCallback(
    (recordsIncluded: number) => {
      if (builderState.isSavedAudience && canSaveOrBuy) {
        updateRecordsIncluded({
          variables: {
            audienceId: builderState.audienceId,
            recordsIncluded,
          },
          onCompleted: () => {
            navigateToPurchasePage(recordsIncluded);
          },
          onError: (err) => {
            setContextMenuError(err.message);
          },
        });
      } else {
        // PR 2d: receive the freshly-saved audienceId from the save
        // callback and pass it through as the override. builderState
        // hasn't re-rendered yet at this point, so reading
        // builderState.audienceId inside navigateToPurchasePage would
        // still see undefined and the server snapshot would be skipped.
        handleSaveAudienceClick(builderState.audienceName, (newAudienceId) => {
          navigateToPurchasePage(recordsIncluded, newAudienceId);
        });
      }
    },
    [
      builderState,
      canSaveOrBuy,
      handleSaveAudienceClick,
      navigateToPurchasePage,
      updateRecordsIncluded,
    ]
  );

  const handleBuyClick = useCallback(
    (recordsIncluded: number) => {
      const isDefaultName = /^Untitled Audience(\s\(\d+\))?$/.test(
        builderState.audienceName.trim()
      );
      if (isDefaultName && !builderState.isSavedAudience) {
        setShowUnchangedNameModal(true);
        return;
      }

      if (audienceWithNameAlreadyExists && !builderState.isSavedAudience) {
        builderDispatch({
          type: SET_AUDIENCE_NAME_ERROR,
          payload: {
            audienceNameError: true,
          },
        });
        return;
      }

      // ATAD-134 option 2. recordsAvailable, not recordsIncluded: someone who
      // dialled the quantity down to 50k from a 200M pool still BUILT a 200M
      // audience, and the point of the warning is that their filters are
      // probably wrong. No staleness risk — Continue Order is disabled unless
      // canSaveOrBuy, which requires fresh counts.
      //
      // Acked by count rather than by a boolean, so re-running counts to a
      // different number warns again.
      if (
        isOversizedAudience(builderState.recordsAvailable, universeSize) &&
        oversizeAckRef.current !== builderState.recordsAvailable
      ) {
        // Park the ARGUMENT, not a closure — the resume path has to buy the
        // exact quantity MyAudience passed up.
        setPendingBuyRecords(recordsIncluded);
        return;
      }

      proceedWithBuy(recordsIncluded);
    },
    [
      audienceWithNameAlreadyExists,
      builderState,
      proceedWithBuy,
      universeSize,
    ]
  );

  const handleOversizeProceed = useCallback(() => {
    oversizeAckRef.current = builderState.recordsAvailable;
    const records = pendingBuyRecords;
    setPendingBuyRecords(null);
    if (records != null) proceedWithBuy(records);
  }, [builderState.recordsAvailable, pendingBuyRecords, proceedWithBuy]);

  useEffect(() => {
    if (success) {
      setTimeout(
        () =>
          dispatchAction({
            type: CLEAR_SUCCESS,
          }),
        5000
      );
    }
  }, [success]);

  useEffect(() => {
    if (contextMenuError) {
      setTimeout(() => setContextMenuError(null), 5000);
    }
  }, [contextMenuError]);

  useEffect(() => {
    if (
      actionState?.success !== undefined &&
      actionState?.nextAction === 'file-upload-success'
    ) {
      dispatchAction({
        type: START_ACTION,
        payload: {
          name: 'Clean up time!',
          component: <UploadFileSuccess dispatchAction={dispatchAction} />,
        },
      });
    }
  }, [actionState]);

  const resetAudienceFn = useCallback(() => {
    try {
      window.history.replaceState({}, document.title);
    } catch (err) {
      console.error('could not clear state');
    }
    resetBuilder();
  }, [resetBuilder]);

  // Legacy BotPress chatbot code (commented out for potential future use)
  /*
  useEffect(() => {
    const bpWidgetContainer = document.getElementById(
      'bp-web-widget-container'
    );

    if (!bpWidgetContainer || !bpWidgetContainer.children.length) {
      const libScriptSrc = 'https://cdn.botpress.cloud/webchat/v1/inject.js';
      const chatScriptSrc =
        'https://mediafiles.botpress.cloud/7d41ced1-f102-4502-84c5-c1577a1c6ac6/webchat/config.js';

      const scripts = Array.from(document.querySelectorAll('script'));
      let libScript: HTMLScriptElement | null = scripts.find((script) =>
        script.src.includes(libScriptSrc)
      ) as HTMLScriptElement | null;
      let chatScript: HTMLScriptElement | null = scripts.find((script) =>
        script.src.includes(chatScriptSrc)
      ) as HTMLScriptElement | null;

      if (!libScript) {
        libScript = document.createElement('script');
        libScript.src = `${libScriptSrc}?ts=${Date.now()}`;
        document.body.appendChild(libScript);
      }

      if (!chatScript) {
        chatScript = document.createElement('script');
        chatScript.src = `${chatScriptSrc}?ts=${Date.now()}`;
        chatScript.defer = true;
        document.body.appendChild(chatScript);
      }

      return () => {
        const bpWidgetContainers = Array.from(
          document.querySelectorAll('#bp-web-widget-container')
        );

        bpWidgetContainers.forEach((bpWidgetContainer) => {
          if (bpWidgetContainer) {
            bpWidgetContainer.remove();
          }
        });

        if (libScript && libScript.parentNode) {
          document.body.removeChild(libScript);
        }
        if (chatScript && chatScript.parentNode) {
          document.body.removeChild(chatScript);
        }
      };
    }
  }, []);
  */

  return (
    <ErrorBoundary
      FallbackComponent={BuilderErrorFallback}
      onReset={() => resetAudienceFn()}
    >
      <TourProvider tours={tours}>
      <DndProvider backend={HTML5Backend}>
        <DocsBotChatbot />
        <div className="relative flex flex-grow max-w-full max-h-full overflow-auto">
          {loading && (
            <>{loading && <InsetLoadingOverlay message="Loading..." />}</>
          )}
          <div className="flex flex-col flex-grow-0 w-72 bg-gray-100 py-3 px-5 rounded-tr-md max-h-full overflow-auto resize-x">
            <div>
              <h4>My Data</h4>
            </div>
            <ul>
              <li>
                <UploadedFilesList
                  onAddFile={handleAddFileFromModal}
                  canAddToZone={canAddFileToZone}
                />
              </li>
              <li>
                {/* ATAD-136: ZIP code files, usable as include/exclude filters. */}
                <DraggableQueryList
                  query={FILES_QUERY}
                  selector="files"
                  resourceType="zip"
                  label="ZIP Code Files"
                  filterItems={zipCodeFilesNewestFirst}
                />
              </li>
              <li>
                {/* ATAD-146: imported CMDA model files, usable as include/
                    exclude filters with a score range. Polls while an import
                    runs so the model becomes draggable when it finishes.
                    Hidden for users who can't import models, unless they
                    still have some from before. */}
                <DraggableQueryList
                  query={FILES_QUERY}
                  selector="files"
                  resourceType="model"
                  label="Model Files"
                  filterItems={modelFilesNewestFirst}
                  pollWhile={anyModelFileImporting}
                  renderItemStatus={modelFileStatus}
                  hideWhenEmpty={!canUseModelFiles(user)}
                />
              </li>
              <li>
                <SavedAudiencesList
                  onSavedAudienceClick={handleSavedAudienceClick}
                  currentAudienceName={
                    builderState.isSavedAudience
                      ? builderState.audienceName
                      : null
                  }
                />
              </li>
              <li>
                <DraggableQueryList
                  query={PURCHASED_AUDIENCES_QUERY}
                  selector="purchasedAudiences"
                  resourceType="audience"
                  label="Purchased Audiences"
                />
              </li>
            </ul>
            <div className="pt-5">
              <button
                className="flex flex-row items-center"
                type="button"
                onClick={() =>
                  dispatchAction({
                    type: START_ACTION,
                    payload: {
                      name: 'Upload File',
                      component: (
                        <FileImportSelector
                          dispatchAction={dispatchAction}
                          allowZipFiles
                          allowDataSources={canUseModelFiles(user)}
                        />
                      ),
                      // ATAD-146: the two-pane sources modal.
                      layout: 'panes',
                      showCancel: true,
                    },
                  })
                }
              >
                <div
                  className={`bg-background-dark w-12 h-12 rounded-full p-3.5`}
                >
                  <img
                    className="w-full h-full"
                    src={uploadIcon}
                    alt="upload icon"
                  />
                </div>
                <div className="pl-3">Upload file</div>
              </button>
            </div>
            <div className="border-b w-full border-black my-5"></div>
            <div>
              <h4 className="mb-3">Data Dictionary</h4>
            </div>
            <div className="flex">
              <DataDictionary dictionary={builderState.dataDictionary} />
            </div>
          </div>
          <div className="flex flex-col flex-grow p-3 pl-5 overflow-auto">
            <div className="flex flex-row items-center">
              <h3 className="mr-5">Build Your Audience</h3>

              <button
                className="font-bold text-blue hover:text-primary mb-4 text-sm"
                type="button"
                onClick={() => {
                  if (
                    hasStateChanged ||
                    (builderState.hasGottenCounts &&
                      !builderState.isSavedAudience)
                  ) {
                    setShowLoadPrompt(true);
                    setConfirmLoadPrompt({
                      loadFn: resetAudienceFn,
                      label: 'Reset',
                    });
                    return;
                  }
                  resetAudienceFn();
                }}
              >
                Reset
              </button>
            </div>
            <div className="flex flex-row pt-2 pb-5">
              <div className="flex flex-col">
                <AudienceNameForm
                  onBlur={(value) =>
                    builderState.audienceName !== value &&
                    builderDispatch({
                      type: AUDIENCE_NAME_CHANGE,
                      payload: {
                        value,
                      },
                    })
                  }
                  onSubmit={handleSaveAudienceClick}
                  initialValue={builderState.audienceName}
                  canSave={canSaveOrBuy}
                  isSavedAudience={builderState.isSavedAudience}
                  hasStateChanged={hasStateChanged}
                  setNameError={(val) =>
                    builderDispatch({
                      type: SET_AUDIENCE_NAME_ERROR,
                      payload: {
                        audienceNameError: val,
                      },
                    })
                  }
                />
                {builderState.audienceNameError && (
                  <ErrorText
                    className="-mb-7 mt-1"
                    message="An audience with that name already exists. Please use a different name."
                  />
                )}
              </div>
            </div>
            <div className="flex flex-row flex-grow space">
              <div className="flex flex-col flex-grow pr-3">
                <GetStartedTourBanner />
                <PlainLanguageSummary
                  expression={builderState.expression}
                  excludeExpression={builderState.excludeExpression}
                />
                <div className="flex flex-row py-2 justify-between">
                  <h4 className="mb-0 pr-4">Include</h4>
                  <div className="flex flex-col 2xl:flex-row 2xl:space-x-6">
                    <label className="cursor-pointer ">
                      <CheckboxInput
                        value={builderState.includeOnlyOneRecordPerHousehold}
                        checked={builderState.includeOnlyOneRecordPerHousehold}
                        onChange={() =>
                          builderDispatch({
                            type: CHECKBOX_CHANGE,
                            payload: {
                              name: 'includeOnlyOneRecordPerHousehold',
                            },
                          })
                        }
                      />{' '}
                      Include only one record per household
                    </label>
                  </div>
                </div>
                <VariablesDropzone
                  dropzoneMessage="Drag and drop desired elements from the left to customize
                    your audience"
                  zoneType="include"
                  builderState={builderState}
                  builderDispatch={builderDispatch}
                />
                <div className="flex flex-row py-2 justify-between pt-6">
                  <h4 className="mb-0 pr-4">Exclude</h4>
                </div>
                <VariablesDropzone
                  dropzoneMessage="Exclude specific variables, lists, or purchased audiences from your audience"
                  zoneType="exclude"
                  builderState={builderState}
                  builderDispatch={builderDispatch}
                />
              </div>
              <MyAudience
                hasStateChanged={hasStateChanged}
                recordsAvailable={recordsAvailable}
                recordsIncluded={recordsIncluded}
                incomingRecordsIncluded={builderState.incomingRecordsIncluded}
                onGetCountsClick={handleGetCountsClick}
                onBuyClick={handleBuyClick}
                error={error}
                canBuy={canSaveOrBuy}
                showOversizeBadge={showOversizeBadge}
                universeSharePct={universeSharePct}
                builderDispatch={builderDispatch}
              />
            </div>
          </div>
          {/*<BuilderHelpfulTipsContainer*/}
          {/*  builderDispatch={builderDispatch}*/}
          {/*  show={showAddGeoHelpfulTip}*/}
          {/*/>*/}
        </div>
        {actionState.inProgress && (
          <ActionModal
            actionState={actionState}
            dispatchAction={dispatchAction}
            show={actionState.inProgress}
          />
        )}
        <ToastNotification
          message={actionState.success}
          success={true}
          show={actionState.success}
          position="top-center"
        />
        <ToastNotification
          message={contextMenuError}
          error={true}
          show={contextMenuError}
          position="top-center"
        />
        <Menu id={VARIABLE_ATTRIBUTE}>
          <Item data={{ zoneType: 'include' }} onClick={handleAttributeClick}>
            Include
          </Item>
          <Item data={{ zoneType: 'exclude' }} onClick={handleAttributeClick}>
            Exclude
          </Item>
        </Menu>
        <Menu id={MY_DATA_ATTRIBUTE}>
          <Item
            data={{ zoneType: 'include' }}
            onClick={handleMyDataResourceClick}
          >
            Include
          </Item>
          <Item
            data={{ zoneType: 'exclude' }}
            onClick={handleMyDataResourceClick}
          >
            Exclude
          </Item>
          <Item
            hidden={({ props }) => {
              return props.prefix === filePrefixesMap.purchased;
            }}
            onClick={handleDeleteClick}
          >
            Delete
          </Item>
        </Menu>
        <Menu id="delete-only">
          <Item onClick={handleDeleteClick}>Delete</Item>
        </Menu>
        {showPrompt && (
          <NavigationPromptModal
            show={showPrompt}
            onConfirm={confirmNavigation}
            onCancel={cancelNavigation}
          />
        )}
        {showLoadPrompt && (
          <NavigationPromptModal
            show={showLoadPrompt}
            onConfirm={() => {
              setShowLoadPrompt(false);
              confirmLoadPrompt.loadFn();
            }}
            onCancel={() => {
              setShowLoadPrompt(false);
              setConfirmLoadPrompt(null);
            }}
            confirmLabel={confirmLoadPrompt.label}
          />
        )}
        {/* Three checks, one component. They stay separate pieces of state
            rather than one merged list because the early-returns above make
            them sequential: the blocking name warning shows first, then the
            advisory oversize warning on the next click. */}
        <AudienceWarningModal
          show={showUnchangedNameModal}
          blocking
          layout="prose"
          heading="Name Your Audience"
          warnings={UNCHANGED_NAME_WARNING}
          cancelLabel="Return to Name Audience"
          onCancel={() => setShowUnchangedNameModal(false)}
        />
        <AudienceWarningModal
          show={wideOpenIssues !== null}
          heading={wideOpenDialogCopy(wideOpenIssues).heading}
          warnings={buildWideOpenWarnings(wideOpenIssues)}
          intro={wideOpenDialogCopy(wideOpenIssues).intro}
          onCancel={() => setWideOpenIssues(null)}
          onProceed={handleWideOpenProceed}
        />
        <AudienceWarningModal
          show={pendingBuyRecords !== null}
          heading="This audience is unusually large"
          layout="prose"
          warnings={buildOversizeWarnings(universeSharePct, universeSize)}
          onCancel={() => setPendingBuyRecords(null)}
          onProceed={handleOversizeProceed}
        />
      </DndProvider>
      </TourProvider>
    </ErrorBoundary>
  );
}
